import { useCallback, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";

import { CardProps, Icon, Loader, ValidationCard } from "@atoms";
import { Button } from "@molecules";
import { P, H2 } from "@quarks";
import {
  getExternalCalendarFeedStatus,
  getExternalCalendarFeedLinks,
  rotateExternalCalendarFeedLinks,
} from "@lib/store";
import { useAppDispatch, useDialog } from "@hooks";

import { CellWrapper, ProfileCard } from "../styles";
import { ActionsRow, ContentContainer, ErrorWrapper, InfoHintIcon, HintText, NoticeText, RetryWrapper } from "./styles";
import { copyTextToClipboard, isAuthError } from "./helpers";
import {
  externalCalendarFeedReducer,
  getExternalCalendarFeedStateSnapshot,
  initialExternalCalendarFeedState,
} from "./state";

export const ExternalCalendarFeedCard = ({ className, style, ...props }: CardProps) => {
  const { t } = useTranslation("profile");
  const { t: tTranslation } = useTranslation("translation");
  const dispatch = useAppDispatch();
  const { destructiveDialog } = useDialog();

  const [state, dispatchState] = useReducer(externalCalendarFeedReducer, initialExternalCalendarFeedState);

  const snapshot = getExternalCalendarFeedStateSnapshot(state);
  const isLoading = state.kind === "loading";
  const isRotating = state.kind === "ready-started" ? state.isRotating : false;
  const loadError =
    state.kind === "error"
      ? t(state.errorKey === "auth" ? "ExternalCalendarFeed.AuthError" : "ExternalCalendarFeed.GenericError")
      : null;
  const hasStarted = snapshot.hasStarted;
  const links = snapshot.links;
  const canRotate = snapshot.canRotate;
  const showRotateNotice = snapshot.showRotateNotice;

  const getErrorKey = (error: unknown): "auth" | "generic" => {
    return isAuthError(error) ? "auth" : "generic";
  };

  const loadLinks = async () => {
    dispatchState({ type: "start-loading-links" });

    try {
      const result = await dispatch(getExternalCalendarFeedLinks()).unwrap();
      dispatchState({ type: "links-loaded", payload: result });
    } catch (error) {
      dispatchState({
        type: "load-failed",
        payload: { errorKey: getErrorKey(error) },
      });
    }
  };

  const loadStatus = useCallback(async () => {
    dispatchState({ type: "start-loading-status" });

    try {
      const result = await dispatch(getExternalCalendarFeedStatus()).unwrap();
      dispatchState({ type: "status-loaded", payload: result });
    } catch (error) {
      dispatchState({
        type: "load-failed",
        payload: { errorKey: getErrorKey(error) },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (state.kind === "loading" && state.request === "status") {
      void loadStatus();
    }
  }, [loadStatus, state.kind, state]);

  const onGetStarted = async () => {
    await loadLinks();
  };

  const onSubscribe = () => {
    if (!links) return;

    window.location.assign(links.webcalUrl);
  };

  const onCopyLink = async () => {
    if (!links) return;

    try {
      await copyTextToClipboard(links.url);
      toast.success(t("ExternalCalendarFeed.CopySuccess"));
    } catch {
      toast.error(t("ExternalCalendarFeed.CopyError"));
    }
  };

  const onRotateLink = async () => {
    const confirmed = await destructiveDialog(
      t("ExternalCalendarFeed.RotateDialogTitle"),
      t("ExternalCalendarFeed.RotateWarning"),
      "warning",
      t("ExternalCalendarFeed.RotateAction"),
    );

    if (!confirmed) return;

    dispatchState({ type: "start-rotating" });

    try {
      const result = await dispatch(rotateExternalCalendarFeedLinks()).unwrap();
      dispatchState({ type: "rotate-success", payload: result });
      toast.success(t("ExternalCalendarFeed.RotateSuccess"));
    } catch (error) {
      dispatchState({ type: "rotate-failed" });
      toast.error(
        t(getErrorKey(error) === "auth" ? "ExternalCalendarFeed.AuthError" : "ExternalCalendarFeed.GenericError"),
      );
    }
  };

  return (
    <ProfileCard
      as="section"
      className={className}
      style={style}
      {...props}>
      <H2>{t("ExternalCalendarFeed.Title")}</H2>

      <CellWrapper>
        <ContentContainer>
          <P>{t("ExternalCalendarFeed.Description")}</P>

          {!hasStarted && !loadError && (
            <HintText>
              <Button
                button="tertiary"
                size="small"
                disabled={isLoading}
                onClick={onGetStarted}>
                {isLoading ? (
                  <Loader
                    className="size-5"
                    color="currentColor"
                  />
                ) : (
                  t("ExternalCalendarFeed.GetStartedAction")
                )}
              </Button>
            </HintText>
          )}

          {hasStarted && (
            <>
              <ActionsRow>
                <Button
                  button="tertiary"
                  size="small"
                  disabled={isLoading || !links}
                  onClick={onSubscribe}>
                  {t("ExternalCalendarFeed.SubscribeAction")}

                  <InfoHintIcon data-tooltip-id="external-calendar-feed-info-tooltip">
                    <Icon
                      icon="info"
                      className="size-4"
                      color="currentColor"
                      aria-label={t("ExternalCalendarFeed.FallbackHint")}
                    />
                  </InfoHintIcon>

                  <Tooltip
                    id="external-calendar-feed-info-tooltip"
                    noArrow
                    delayShow={250}>
                    <P>{t("ExternalCalendarFeed.FallbackHint")}</P>
                  </Tooltip>
                </Button>

                <Button
                  button="tertiary"
                  size="small"
                  disabled={isLoading || !links}
                  onClick={onCopyLink}>
                  {t("ExternalCalendarFeed.CopyAction")}
                </Button>

                <Button
                  button="secondary destructive"
                  size="small"
                  disabled={isLoading || isRotating || !links || !canRotate}
                  onClick={onRotateLink}>
                  {isRotating ? (
                    <Loader
                      className="size-5"
                      color="currentColor"
                    />
                  ) : (
                    t("ExternalCalendarFeed.RotateAction")
                  )}
                </Button>
              </ActionsRow>

              {showRotateNotice && (
                <NoticeText>
                  <P>{t("ExternalCalendarFeed.RotateReminder")}</P>
                </NoticeText>
              )}
            </>
          )}

          {loadError && (
            <ErrorWrapper>
              <ValidationCard>
                <div>
                  <P>{loadError}</P>

                  <RetryWrapper>
                    <Button
                      button="tertiary"
                      size="small"
                      disabled={isLoading}
                      onClick={hasStarted ? loadLinks : loadStatus}>
                      {isLoading ? (
                        <Loader
                          className="size-5"
                          color="currentColor"
                        />
                      ) : (
                        tTranslation("Retry")
                      )}
                    </Button>
                  </RetryWrapper>
                </div>
              </ValidationCard>
            </ErrorWrapper>
          )}
        </ContentContainer>
      </CellWrapper>
    </ProfileCard>
  );
};
